import React, {
  useState,
  forwardRef,
  useRef,
  type InputHTMLAttributes,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon, InputPrimitive, isWeb } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";
import { CheckCr, Remove } from "@xsolla/xui-icons-base";

export interface InputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange">,
    ThemeOverrideProps {
  /**
   * Property for specifying the value of the control.
   */
  value?: string;
  /**
   * Property for specifying the placeholder of the control.
   */
  placeholder?: string;
  /**
   * Event handler when the value changes (for controlled mode).
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Event handler when the text changes (alternative to onChange).
   */
  onChangeText?: (text: string) => void;
  /**
   * Property for changing the size of the input.
   */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /**
   * Property for disabling the control.
   */
  disabled?: boolean;
  /**
   * Property for displaying a label above the input.
   */
  label?: string;
  /**
   * Property for displaying an error message and highlighting the control as invalid.
   */
  errorMessage?: string;
  /**
   * Property for displaying an error and highlighting the control as invalid.
   */
  error?: boolean;
  /**
   * Property to display an icon on the left side.
   */
  iconLeft?: React.ReactNode;
  /**
   * Property to display an icon on the right side.
   */
  iconRight?: React.ReactNode;
  /**
   * Add function clear for input.
   */
  extraClear?: boolean;
  /**
   * Function triggered when user clicks on extraClear button.
   */
  onRemove?: () => void;
  /**
   * Property for show checked status in input. Show only if not errorMessage.
   */
  checked?: boolean;
  /**
   * Property for passing a new icon to replace the default checked icon.
   */
  checkedIcon?: React.ReactNode;
  /**
   * Property to specify the size of the right icon.
   */
  iconRightSize?: number | string;
  /**
   * Unique identifier for the input element. Used for accessibility linking.
   */
  id?: string;
  /**
   * Accessible label for screen readers when no visible label is present.
   */
  "aria-label"?: string;
  /**
   * Override border radius for top-left corner.
   */
  borderTopLeftRadius?: number;
  /**
   * Override border radius for top-right corner.
   */
  borderTopRightRadius?: number;
  /**
   * Override border radius for bottom-left corner.
   */
  borderBottomLeftRadius?: number;
  /**
   * Override border radius for bottom-right corner.
   */
  borderBottomRightRadius?: number;
  /**
   * Custom background color for the input.
   */
  backgroundColor?: string;
  testID?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      placeholder,
      onChange,
      onChangeText,
      onKeyDown,
      size = "md",
      disabled = false,
      name,
      label,
      errorMessage,
      error,
      iconLeft,
      iconRight,
      iconRightSize,
      extraClear = false,
      onRemove,
      checked = false,
      checkedIcon = <CheckCr />,
      type,
      id: providedId,
      "aria-label": ariaLabel,
      borderTopLeftRadius: borderTopLeftRadiusOverride,
      borderTopRightRadius: borderTopRightRadiusOverride,
      borderBottomLeftRadius: borderBottomLeftRadiusOverride,
      borderBottomRightRadius: borderBottomRightRadiusOverride,
      backgroundColor: backgroundColorProp,
      testID,
      onBlur: externalOnBlur,
      onFocus: externalOnFocus,
      themeMode,
      themeProductContext,
      ...rest
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const [internalState, setInternalState] = useState<"default" | "focus">(
      "default"
    );
    const [passValue, setPassValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    // Sanitize useId() output to remove colons (e.g., :r0: -> r0)
    // This ensures valid HTML id attributes and ARIA references
    const rawId = useId();
    const safeId = rawId.replace(/:/g, "");
    const inputId = providedId || `input-${safeId}`;
    const labelId = `${inputId}-label`;
    const errorId = `${inputId}-error`;

    React.useImperativeHandle(
      ref,
      () => inputRef.current as HTMLInputElement,
      []
    );

    const isDisable = disabled;
    const isError = !!(errorMessage || error);
    const isFocus = internalState === "focus";

    const isLeftInputIconShown = !!iconLeft;
    const isRightInputIconShown = !!iconRight;

    // Handle checked status (only show if not error)
    const isCheckedShown = checked && !errorMessage;
    const isExtraClearIconShown =
      !disabled && extraClear && !!(value !== undefined ? value : passValue);
    const extrasCount =
      Number(isExtraClearIconShown) +
      Number(isCheckedShown) +
      Number(isRightInputIconShown);

    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!isDisable) {
        setInternalState("focus");
      }
      externalOnFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!isDisable) {
        setInternalState("default");
      }
      externalOnBlur?.(e);
    };

    // Match switch-repo handleChange pattern exactly
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (onChange) {
        onChange(e);
      }
      if (onChangeText) {
        onChangeText(newValue);
      }

      setPassValue(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.currentTarget?.blur();
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    // Match switch-repo onClear pattern - NO direct DOM manipulation
    const handleClear = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      // Update internal state first (this makes the input show empty)
      setPassValue("");

      onRemove?.();

      // Create synthetic change event and call onChange if provided
      if (inputRef.current) {
        // Web-specific: Use the native setter to properly set the value
        // without shadowing the property (only on web platform)
        if (typeof window !== "undefined" && window.HTMLInputElement) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            "value"
          )?.set;

          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(inputRef.current, "");
          }
        }

        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
          type: "change",
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
        inputRef.current.focus();
      }
    };

    let backgroundColor = backgroundColorProp || inputColors.bg;
    let borderColor = inputColors.border;
    let outlineColor: string | undefined;

    if (isDisable) {
      backgroundColor = inputColors.bgDisable;
      borderColor = inputColors.borderDisable;
    } else if (isError) {
      outlineColor = theme.colors.border.alert;
      if (isFocus) {
        backgroundColor = theme.colors.control.focus.bg;
      }
    } else if (isFocus) {
      backgroundColor = theme.colors.control.focus.bg;
      outlineColor = theme.colors.border.brand;
    }

    const textColor = isDisable ? inputColors.textDisable : inputColors.text;
    const placeholderColor = inputColors.placeholder;
    // Adornment icons (leading, trailing, clear) use the muted placeholder tint
    // per Figma (control/input/placeholder), not the full-weight text colour.
    // The checked ✓ keeps its semantic success colour. See FEP-877.
    const iconColor = isDisable ? inputColors.textDisable : placeholderColor;

    // Padding values from Figma design
    const paddingConfig = {
      xl: { vertical: 12, horizontal: 12 },
      lg: { vertical: 14, horizontal: 12 },
      md: { vertical: 11, horizontal: 12 },
      sm: { vertical: 7, horizontal: 10 },
      xs: { vertical: 7, horizontal: 10 },
    };

    // Icon sizes from Figma design
    const iconSizeConfig = {
      xl: 18,
      lg: 18,
      md: 18,
      sm: 16,
      xs: 16,
    };

    // Focus outline config from Figma design
    const focusOutlineConfig = {
      xl: { width: 1, offset: -1 },
      lg: { width: 1, offset: -1 },
      md: { width: 1, offset: -1 },
      sm: { width: 1, offset: -1 },
      xs: { width: 1, offset: -1 },
    };

    const padding = paddingConfig[size];
    const borderRadius = theme.shape.input[size].borderRadius;
    const iconSize = iconSizeConfig[size];
    const focusOutline = focusOutlineConfig[size];

    // A hidden input carries no visual chrome (no label, border, background, or
    // extras). Render only the bare field so nothing is shown on screen.
    if (type === "hidden") {
      return (
        <InputPrimitive
          ref={inputRef}
          id={inputId}
          type="hidden"
          name={name}
          value={value}
          onChange={handleChange}
          disabled={isDisable}
          data-testid={testID}
          {...rest}
        />
      );
    }

    return (
      <Box flexDirection="column" gap={sizeStyles.fieldGap} width="100%">
        {label && (
          <Box as="label" id={labelId}>
            <Text
              color={theme.colors.content.secondary}
              fontSize={sizeStyles.fontSize - 2}
              fontWeight="500"
            >
              {label}
            </Text>
          </Box>
        )}
        <Box
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={borderColor !== "transparent" ? 1 : 0}
          height={sizeStyles.height}
          paddingVertical={padding.vertical}
          paddingHorizontal={padding.horizontal}
          flexDirection="row"
          alignItems="center"
          gap={10}
          position="relative"
          cursor={isDisable ? "not-allowed" : "text"}
          onPress={() => {
            if (!isDisable) inputRef.current?.focus();
          }}
          data-testid="input__container"
          style={{
            borderTopLeftRadius: borderTopLeftRadiusOverride ?? borderRadius,
            borderTopRightRadius: borderTopRightRadiusOverride ?? borderRadius,
            borderBottomLeftRadius:
              borderBottomLeftRadiusOverride ?? borderRadius,
            borderBottomRightRadius:
              borderBottomRightRadiusOverride ?? borderRadius,
            ...(outlineColor && isWeb
              ? {
                  outline: `${focusOutline.width}px solid ${outlineColor}`,
                  outlineOffset: `${focusOutline.offset}px`,
                }
              : outlineColor && !isWeb
                ? { borderColor: outlineColor, borderWidth: focusOutline.width }
                : {}),
          }}
          hoverStyle={
            !isDisable && !isFocus && !isError
              ? {
                  backgroundColor: inputColors.bgHover,
                  borderColor: inputColors.borderHover,
                }
              : undefined
          }
        >
          {isLeftInputIconShown && (
            <Box alignItems="center" justifyContent="center">
              <Icon size={iconSize} color={iconColor}>
                {iconLeft}
              </Icon>
            </Box>
          )}

          <Box flex={1} height="100%" justifyContent="center">
            <InputPrimitive
              ref={inputRef}
              id={inputId}
              value={value}
              name={name}
              placeholder={placeholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={isDisable}
              type={type || "text"}
              color={textColor}
              fontSize={sizeStyles.fontSize}
              fontFamily={theme.fonts.body}
              placeholderTextColor={placeholderColor}
              aria-invalid={isError || undefined}
              aria-describedby={errorMessage ? errorId : undefined}
              aria-labelledby={label ? labelId : undefined}
              aria-label={!label ? ariaLabel : undefined}
              aria-disabled={isDisable || undefined}
              data-testid={testID}
              {...rest}
            />
          </Box>

          {/* Right-side Extras Wrapper */}
          {extrasCount > 0 && (
            <Box flexDirection="row" alignItems="center" gap={4}>
              {isExtraClearIconShown && (
                <Box
                  as="button"
                  type="button"
                  alignItems="center"
                  justifyContent="center"
                  width={iconSize}
                  height="100%"
                  backgroundColor="transparent"
                  borderWidth={0}
                  cursor={disabled ? "not-allowed" : "pointer"}
                  {...(isWeb && {
                    onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) =>
                      e.preventDefault(),
                  })}
                  onPress={!disabled ? handleClear : undefined}
                  disabled={disabled}
                  data-testid="input__extra-clear-button"
                >
                  <Icon size={iconSize} color={iconColor}>
                    <Remove />
                  </Icon>
                </Box>
              )}
              {isCheckedShown && (
                <Box
                  alignItems="center"
                  justifyContent="center"
                  width={iconSize}
                  height="100%"
                >
                  <Icon
                    size={iconSize}
                    color={theme.colors.content.success.primary}
                  >
                    {checkedIcon}
                  </Icon>
                </Box>
              )}
              {isRightInputIconShown && (
                <Box alignItems="center" justifyContent="center">
                  <Icon size={iconRightSize || iconSize} color={iconColor}>
                    {iconRight}
                  </Icon>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Text
            id={errorId}
            role="alert"
            color={theme.colors.content.alert.primary}
            fontSize={sizeStyles.fontSize - 2}
            style={{ lineHeight: sizeStyles.lineHeight + "px" }}
          >
            {errorMessage}
          </Text>
        )}
      </Box>
    );
  }
);

Input.displayName = "Input";
